import type { InventoryState } from '@shefira/shared';

import { actionKeyLabel } from '../settings/keybindings.js';
import {
  type CoachJournal,
  type CoachScreen,
  type CoachStep,
  coachStep,
  controlsDue,
} from './camp-coach-steps.js';
import { closeButton } from './hud.js';
import { uiFormat, uiText } from './text.js';
import { isTouch } from './touch.js';
import './camp-coach.css';

/**
 * The camp's coach on screen (ADR 0078): a line under the minimap
 * saying the next thing to do, and a pulsing glow on the very button, bag
 * slot or shop row it asks for. It reads the windows from the page itself,
 * a few times a second, so no window has to tell it anything; it only lives
 * in the camp, and leaves with it.
 *
 * Under the line, from the arrival to the first rat, the basic controls
 * with the player's own keys (or the touches on a touch screen). Their cross
 * puts them away for this character, in this browser.
 */

const GLOW = 'coach-glow';
const EVERY_MS = 250;
const CONTROLS_SEEN_KEY = 'shefira.camp-controls-closed';

function controlsClosed(characterId: string): boolean {
  try {
    return localStorage.getItem(`${CONTROLS_SEEN_KEY}.${characterId}`) === '1';
  } catch {
    return false;
  }
}

function closeControls(characterId: string): void {
  try {
    localStorage.setItem(`${CONTROLS_SEEN_KEY}.${characterId}`, '1');
  } catch {
    /* shown again next visit */
  }
}

/** The controls in the player's language, with the keys they walk and fight with. */
function controlsLines(touch: boolean): string[] {
  if (touch) return [uiText('Touche le sol pour marcher, touche une créature pour l’attaquer.')];
  const walk = (['walkUp', 'walkLeft', 'walkDown', 'walkRight'] as const)
    .map(actionKeyLabel)
    .join(' ');
  return [
    uiFormat('Clic ou {keys} : marcher', { keys: walk }),
    uiFormat('{key} : cibler et attaquer', { key: actionKeyLabel('target') }),
    uiFormat('{key} : changer de cible', { key: actionKeyLabel('targetNext') }),
    uiText('Clic sur un butin : ramasser'),
  ];
}

export interface CampCoachOptions {
  journal: CoachJournal;
  inventory: () => InventoryState | undefined;
  /**
   * Bring a newcomer to the tutor's first words, once, as they arrive:
   * true once done (the tutor may not be in sight yet).
   */
  greet: () => boolean;
  /** Whose controls these are: closed once, they stay closed for this character. */
  characterId: string;
}

function shown(selector: string): boolean {
  const element = document.querySelector<HTMLElement>(selector);
  return element !== null && !element.hidden;
}

function screenNow(): CoachScreen {
  const merchant = shown('.merchant')
    ? document.querySelector<HTMLElement>('.merchant__tab--active')?.dataset.mode
    : undefined;
  return {
    dialog: shown('.quest-dialog'),
    merchant: merchant === 'buy' || merchant === 'sell' ? merchant : null,
    inventory: shown('.inventory'),
    fusion: shown('.fusion'),
    forge: shown('[data-panel="forge"]'),
  };
}

export class CampCoach {
  private readonly bubble = document.createElement('div');
  private readonly line = document.createElement('p');
  private readonly controls = document.createElement('ul');
  private readonly close: HTMLButtonElement;
  private lastControls = '';
  private readonly timer: number;
  private lit: HTMLElement[] = [];
  private lastText = '';
  private greeted = false;

  constructor(private readonly options: CampCoachOptions) {
    this.bubble.className = 'camp-coach';
    this.bubble.setAttribute('role', 'status');
    this.bubble.setAttribute('aria-live', 'polite');
    this.bubble.hidden = true;
    this.line.className = 'camp-coach__line';
    this.controls.className = 'camp-coach__controls';
    this.controls.setAttribute('aria-label', uiText('Commandes'));
    this.controls.hidden = true;
    this.close = closeButton('Masquer les commandes', () => {
      closeControls(options.characterId);
      this.refresh();
    });
    this.close.classList.add('camp-coach__close');
    this.close.hidden = true;
    this.bubble.append(this.line, this.controls, this.close);
    document.body.append(this.bubble);
    this.timer = window.setInterval(() => {
      this.refresh();
    }, EVERY_MS);
  }

  refresh(): void {
    const { journal } = this.options;
    // The first quest not taken yet: walk the newcomer to the tutor, once.
    if (!this.greeted && journal.known) {
      const fresh = !journal.isDone('camp-rats') && journal.entry('camp-rats') === undefined;
      this.greeted = !fresh || this.options.greet();
    }
    const touch = isTouch();
    this.show(
      coachStep({
        journal,
        inventory: this.options.inventory(),
        screen: screenNow(),
        touch,
      }),
    );
    this.showControls(
      controlsDue(journal) && !controlsClosed(this.options.characterId)
        ? controlsLines(touch)
        : [],
    );
  }

  destroy(): void {
    window.clearInterval(this.timer);
    this.light([]);
    this.bubble.remove();
  }

  private show(step: CoachStep | null): void {
    if (step === null) {
      this.bubble.hidden = true;
      this.light([]);
      return;
    }
    const text = uiText(step.text);
    if (text !== this.lastText) {
      this.lastText = text;
      this.line.textContent = text;
      // Restart the arrival animation for a new instruction only.
      this.bubble.classList.remove('camp-coach--new');
      this.bubble.getBoundingClientRect();
      this.bubble.classList.add('camp-coach--new');
    }
    this.bubble.hidden = false;
    this.light(
      step.targets.flatMap((selector) => [...document.querySelectorAll<HTMLElement>(selector)]),
    );
  }

  private showControls(lines: string[]): void {
    const shown = lines.length > 0;
    this.controls.hidden = !shown;
    this.close.hidden = !shown;
    this.bubble.classList.toggle('camp-coach--controls', shown);
    const joined = lines.join('\n');
    if (joined === this.lastControls) return;
    this.lastControls = joined;
    this.controls.replaceChildren(
      ...lines.map((line) => {
        const item = document.createElement('li');
        item.textContent = line;
        return item;
      }),
    );
  }

  private light(elements: HTMLElement[]): void {
    for (const element of this.lit) {
      if (!elements.includes(element)) element.classList.remove(GLOW);
    }
    for (const element of elements) element.classList.add(GLOW);
    this.lit = elements;
  }
}
